import React, { useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, TextField, Typography, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';

const CreateProjectPage: React.FC = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { refreshProjects } = useProjectContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ name, description })
      });
      const result = await response.json();
      if (result.success) {
        await refreshProjects();
        navigate('/dashboard');
      } else {
        setError(result.error || 'Failed to create project');
      }
    } catch (err) {
      setError('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 3, pt: 3, pb: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Create a New Project
      </Typography>
      <Card elevation={3} sx={{ ml: 0, width: '100%' }}>
        <CardContent sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: 600, width: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Create a New Project
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Projects are required to organize your scripts, tests, and reports.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <TextField
                sx={{ maxWidth: 400, width: '100%' }}
                label="Project Name"
                value={name}
                onChange={e => setName(e.target.value)}
                margin="normal"
                required
                autoFocus
              />
              <TextField
                sx={{ maxWidth: 400, width: '100%' }}
                label="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </Box>
            </form>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateProjectPage; 